import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { supabase } from '../../config/supabase';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import UnlockModal from '../LinkDirectory/UnlockModal';

const { FiArrowLeft, FiSave, FiCheck, FiLoader, FiUser, FiSend, FiAlertCircle } = FiIcons;

const SettingsPage = ({ user }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [recordExists, setRecordExists] = useState(false);
  const [recordId, setRecordId] = useState(null);
  const [testingWebhook, setTestingWebhook] = useState(false);
  const [webhookResult, setWebhookResult] = useState(null);
  const [unlockModalOpen, setUnlockModalOpen] = useState(false);
  const [directoryAccess, setDirectoryAccess] = useState(null);
  const [showCancelTooltip, setShowCancelTooltip] = useState(false);
  const [formData, setFormData] = useState({
    default_name: '',
    default_title: '',
    default_company: '',
    default_email: '',
    default_website_url: '',
    webhook_url: ''
  });
  const [validation, setValidation] = useState({
    default_name: true,
    default_email: true,
    default_website_url: true,
    webhook_url: true
  });

  useEffect(() => {
    if (user?.id) {
      loadUserDefaults();
      loadSubscriptionStatus();
    }
  }, [user?.id]);

  const loadUserDefaults = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('user_outreach_defaults_k9l8x3')
        .select('*')
        .eq('user_id', user.id)
        .single();

      if (error && error.code !== 'PGRST116') {
        // PGRST116 is "no rows returned" error
        console.error('Error loading user defaults:', error);
        setError('Failed to load your personalization settings. Please try again.');
      } else if (data) {
        setFormData({
          default_name: data.default_name || '',
          default_title: data.default_title || '',
          default_company: data.default_company || '',
          default_email: data.default_email || '',
          default_website_url: data.default_website_url || '',
          webhook_url: data.webhook_url || ''
        });
        setRecordExists(true);
        setRecordId(data.id);
      }
    } catch (error) {
      console.error('Error in loadUserDefaults:', error);
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const loadSubscriptionStatus = async () => {
    try {
      const { data, error } = await supabase
        .from('user_directory_access_ld92x4')
        .select('*')
        .eq('user_id', user.id)
        .single();

      if (error && error.code !== 'PGRST116') {
        console.error('Error loading subscription status:', error);
      }

      if (data) {
        setDirectoryAccess(data);
      } else {
        setDirectoryAccess(null);
      }
    } catch (e) {
      console.error('Unexpected error loading subscription status:', e);
      setDirectoryAccess(null);
    }
  };

  const validateForm = () => {
    const newValidation = {
      default_name: !!formData.default_name.trim(),
      default_email: !formData.default_email || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.default_email),
      default_website_url: !formData.default_website_url || /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w.-]*)*\/?$/.test(formData.default_website_url),
      webhook_url: !formData.webhook_url || /^https?:\/\/([\da-z.-]+)\.([a-z.]{2,6})([/\w.-]*)*\/?$/.test(formData.webhook_url)
    };
    setValidation(newValidation);
    return Object.values(newValidation).every(valid => valid);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear success message when form is modified
    if (success) {
      setSuccess(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      setError('Please correct the validation errors before saving.');
      return;
    }

    setError('');
    setSaving(true);
    try {
      if (recordExists) {
        // Update existing record
        const { error: updateError } = await supabase
          .from('user_outreach_defaults_k9l8x3')
          .update({
            default_name: formData.default_name,
            default_title: formData.default_title,
            default_company: formData.default_company,
            default_email: formData.default_email,
            default_website_url: formData.default_website_url,
            webhook_url: formData.webhook_url,
            updated_at: new Date().toISOString()
          })
          .eq('id', recordId);

        if (updateError) {
          throw updateError;
        }
      } else {
        // Insert new record
        const { error: insertError } = await supabase
          .from('user_outreach_defaults_k9l8x3')
          .insert([{
            user_id: user.id,
            default_name: formData.default_name,
            default_title: formData.default_title,
            default_company: formData.default_company,
            default_email: formData.default_email,
            default_website_url: formData.default_website_url,
            webhook_url: formData.webhook_url,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          }]);

        if (insertError) {
          throw insertError;
        }
        // Record now exists for future updates
        setRecordExists(true);
      }

      // Show success message
      setSuccess(true);
      // Auto-hide success message after 3 seconds
      setTimeout(() => {
        setSuccess(false);
      }, 3000);
    } catch (error) {
      console.error('Error saving settings:', error);
      setError('Failed to save settings. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleBack = () => {
    navigate('/dashboard');
  };

  const testWebhook = async () => {
    if (!formData.webhook_url || !validation.webhook_url) {
      setError('Please enter a valid webhook URL before testing.');
      return;
    }

    setTestingWebhook(true);
    setWebhookResult(null);
    
    try {
      const response = await fetch(formData.webhook_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: "contact@example.com",
          subject: "Test Subject",
          message: "This is a test message from K9 Links webhook integration."
        })
      });

      if (response.ok) {
        setWebhookResult({
          success: true,
          message: `Webhook test successful! Status: ${response.status}`
        });
      } else {
        setWebhookResult({
          success: false,
          message: `Webhook test failed. Status: ${response.status}`
        });
      }
    } catch (error) {
      console.error('Error testing webhook:', error);
      setWebhookResult({
        success: false,
        message: `Webhook test failed: ${error.message}`
      });
    } finally {
      setTestingWebhook(false);
      
      // Auto-hide webhook result after 5 seconds
      setTimeout(() => {
        setWebhookResult(null);
      }, 5000);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center mb-8">
          <button
            onClick={handleBack}
            className="flex items-center px-3 py-2 text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white transition-colors mr-4"
          >
            <SafeIcon icon={FiArrowLeft} className="w-4 h-4 mr-1" />
            Back
          </button>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Account Settings</h1>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl shadow-lg p-6 dark:bg-gray-800"
        >
          <div className="flex items-center mb-6">
            <div className="bg-[#D59A4C20] p-3 rounded-lg mr-3">
              <SafeIcon icon={FiUser} className="w-6 h-6 text-[#D59A4C]" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Default Outreach Personalization Settings</h2>
              <p className="text-gray-600 dark:text-gray-400">
                These values personalize your outreach content and represent the main website you promote. You can override them on each list of opportunities if you're building links for more than one brand.
              </p>
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#D59A4C]"></div>
            </div>
          ) : (
            <>
              {error && (
                <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-6 dark:bg-red-900/30 dark:border-red-600">
                  <p className="text-red-700 dark:text-red-400">{error}</p>
                </div>
              )}

              {success && (
                <div className="bg-green-50 border-l-4 border-green-400 p-4 mb-6 dark:bg-green-900/30 dark:border-green-600">
                  <div className="flex items-center">
                    <SafeIcon icon={FiCheck} className="w-5 h-5 text-green-600 dark:text-green-400 mr-2" />
                    <p className="text-green-700 dark:text-green-400">Settings saved successfully!</p>
                  </div>
                </div>
              )}

              {webhookResult && (
                <div className={`${webhookResult.success ? 'bg-green-50 border-green-400 dark:bg-green-900/30 dark:border-green-600' : 'bg-red-50 border-red-400 dark:bg-red-900/30 dark:border-red-600'} border-l-4 p-4 mb-6`}>
                  <div className="flex items-center">
                    <SafeIcon 
                      icon={webhookResult.success ? FiCheck : FiAlertCircle} 
                      className={`w-5 h-5 ${webhookResult.success ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'} mr-2`} 
                    />
                    <p className={`${webhookResult.success ? 'text-green-700 dark:text-green-400' : 'text-red-700 dark:text-red-400'}`}>
                      {webhookResult.message}
                    </p>
                  </div>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label htmlFor="default_name" className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">
                    Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="default_name"
                    name="default_name"
                    type="text"
                    value={formData.default_name}
                    onChange={handleChange}
                    className={`w-full px-4 py-2 border ${!validation.default_name ? 'border-red-500 dark:border-red-700' : 'border-gray-300 dark:border-gray-600'} rounded-lg focus:ring-2 focus:ring-[#D59A4C] focus:border-transparent dark:bg-gray-700 dark:text-white`}
                    placeholder="Your full name"
                    required
                  />
                  {!validation.default_name && (
                    <p className="mt-1 text-sm text-red-600 dark:text-red-400">Name is required</p>
                  )}
                </div>

                <div>
                  <label htmlFor="default_title" className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">
                    Professional Title
                  </label>
                  <input
                    id="default_title"
                    name="default_title"
                    type="text"
                    value={formData.default_title}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#D59A4C] focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    placeholder="e.g. Marketing Manager"
                  />
                </div>

                <div>
                  <label htmlFor="default_company" className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">
                    Company
                  </label>
                  <input
                    id="default_company"
                    name="default_company"
                    type="text"
                    value={formData.default_company}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#D59A4C] focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    placeholder="Your company name"
                  />
                </div>

                <div>
                  <label htmlFor="default_email" className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">
                    Email
                  </label>
                  <input
                    id="default_email"
                    name="default_email"
                    type="email"
                    value={formData.default_email}
                    onChange={handleChange}
                    className={`w-full px-4 py-2 border ${!validation.default_email ? 'border-red-500 dark:border-red-700' : 'border-gray-300 dark:border-gray-600'} rounded-lg focus:ring-2 focus:ring-[#D59A4C] focus:border-transparent dark:bg-gray-700 dark:text-white`}
                    placeholder="you@example.com"
                  />
                  {!validation.default_email && (
                    <p className="mt-1 text-sm text-red-600 dark:text-red-400">Please enter a valid email address</p>
                  )}
                </div>

                <div>
                  <label htmlFor="default_website_url" className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">
                    Website
                  </label>
                  <input
                    id="default_website_url"
                    name="default_website_url"
                    type="url"
                    value={formData.default_website_url}
                    onChange={handleChange}
                    className={`w-full px-4 py-2 border ${!validation.default_website_url ? 'border-red-500 dark:border-red-700' : 'border-gray-300 dark:border-gray-600'} rounded-lg focus:ring-2 focus:ring-[#D59A4C] focus:border-transparent dark:bg-gray-700 dark:text-white`}
                    placeholder="https://example.com"
                  />
                  {!validation.default_website_url && (
                    <p className="mt-1 text-sm text-red-600 dark:text-red-400">Please enter a valid website URL</p>
                  )}
                </div>

                <div>
                  <label htmlFor="webhook_url" className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">
                    Webhook URL
                  </label>
                  <div className="flex gap-2">
                    <input
                      id="webhook_url"
                      name="webhook_url"
                      type="url"
                      value={formData.webhook_url}
                      onChange={handleChange}
                      className={`flex-1 px-4 py-2 border ${!validation.webhook_url ? 'border-red-500 dark:border-red-700' : 'border-gray-300 dark:border-gray-600'} rounded-lg focus:ring-2 focus:ring-[#D59A4C] focus:border-transparent dark:bg-gray-700 dark:text-white`}
                      placeholder="https://example.com/webhook"
                    />
                    <button
                      type="button"
                      onClick={testWebhook}
                      disabled={testingWebhook || !formData.webhook_url || !validation.webhook_url}
                      className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors blue-btn"
                    >
                      {testingWebhook ? (
                        <>
                          <SafeIcon icon={FiLoader} className="w-4 h-4 mr-2 animate-spin" />
                          Testing...
                        </>
                      ) : (
                        <>
                          <SafeIcon icon={FiSend} className="w-4 h-4 mr-2" />
                          Test Webhook
                        </>
                      )}
                    </button>
                  </div>
                  {!validation.webhook_url && formData.webhook_url && (
                    <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                      Please enter a valid URL starting with http:// or https://
                    </p>
                  )}
                </div>

                <div className="pt-4">
                  <button
                    type="submit"
                    disabled={saving}
                    className="flex items-center px-6 py-3 bg-[#D59A4C] text-white rounded-lg hover:bg-[#B58C54] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {saving ? (
                      <>
                        <SafeIcon icon={FiLoader} className="w-5 h-5 mr-2 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <SafeIcon icon={FiSave} className="w-5 h-5 mr-2" />
                        Save Settings
                      </>
                    )}
                  </button>
                </div>
              </form>

              {/* Subscription Management */}
              <div className="mt-10 border-t border-gray-200 pt-6 dark:border-gray-700">
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">Subscription Management</h3>
                {(() => {
                  const SUBSCRIPTION_PLANS = [
                    { id: 'price_1RxUwGI8bx27Wh5OX4QQ1TMR', name: 'Pro Plan', price: 29, credits: 35, savings: '21% savings', description: '35 credits + Pro Links access' },
                    { id: 'price_1RxUyII8bx27Wh5OVnaoGdLq', name: 'Agency Plan', price: 79, credits: 100, savings: '27% savings', description: '100 credits + Pro Links access' }
                  ];

                  if (!directoryAccess) {
                    return (
                      <p className="text-gray-700 dark:text-gray-300">
                        No active subscription,{' '}
                        <button type="button" onClick={() => setUnlockModalOpen(true)} className="text-blue-600 hover:text-blue-700 no-border">
                          view plan options
                        </button>
                        .
                      </p>
                    );
                  }

                  const activePlan = SUBSCRIPTION_PLANS.find(p => p.id === directoryAccess.subscription_price_id);
                  if (activePlan) {
                    return (
                      <div className="text-gray-700 dark:text-gray-300">
                        <p>
                            <strong>Active Subscription:</strong> {activePlan.name} <br />
                            ${activePlan.price}/mo ({activePlan.savings}) <br />
                            {activePlan.description}
                        </p>

                        <div className="mt-3 relative inline-block"
                             onMouseEnter={() => setShowCancelTooltip(true)}
                             onMouseLeave={() => setShowCancelTooltip(false)}
                        >
                          <button
                            type="button"
                            onClick={async () => {
                              try {
                                // Retrieve current user's JWT from Supabase session
                                const { data: { session } } = await supabase.auth.getSession();
                                const token = session?.access_token;

                                if (!token) {
                                  setWebhookResult({ success: false, message: 'You must be logged in to cancel your subscription.' });
                                  return;
                                }

                                // Optimistic UI update after successful cancel
                                const response = await fetch('/.netlify/functions/cancel-subscription', {
                                  method: 'POST',
                                  headers: {
                                    'Content-Type': 'application/json',
                                    'Authorization': `Bearer ${token}`
                                  },
                                  body: JSON.stringify({ user_id: user.id })
                                });
                                if (response.status === 401) {
                                  setWebhookResult({ success: false, message: 'You must be logged in to cancel your subscription.' });
                                  return;
                                }
                                if (response.ok) {
                                  // Show success immediately; webhook will finalize DB state
                                  setWebhookResult({ success: true, message: 'Subscription canceled successfully.' });
                                  // Optionally refresh local state shortly after
                                  setTimeout(() => { loadSubscriptionStatus(); }, 1500);
                                } else {
                                  const err = await response.json().catch(() => ({}));
                                  setWebhookResult({ success: false, message: err.error || 'Failed to cancel subscription.' });
                                }
                              } catch (e) {
                                setWebhookResult({ success: false, message: 'Failed to cancel subscription.' });
                              }
                            }}
                            className="py-2 bg-transparent text-red-700 transition-colors no-border"
                          >
                            Cancel Subscription
                          </button>
                          {showCancelTooltip && (
                            <div className="fixed left-1/2 -translate-x-1/2 bottom-4 z-10 max-w-[calc(100vw-2rem)] sm:absolute sm:left-0 sm:bottom-full sm:top-auto sm:translate-x-0 sm:mb-2 sm:w-72 p-2 rounded shadow-lg border border-gray-200 dark:border-gray-700 text-sm text-left break-words text-red-700"
                              style={{ backgroundColor: '#ffb09c' }}
                            >
                              Warning: cancelling your subscription revokes access to the pro link directory. If you would like to speak to someone before deciding whether or not to cancel, please start a chat and ask to speak to a human. We want you to have a great experience and would like to make sure you are getting the most out of your subscription.
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  }

                  return (
                    <p className="text-gray-700 dark:text-gray-300">
                      Active Subscription
                    </p>
                  );
                })()}
              </div>
            </>
          )}
        </motion.div>
      </div>

      <UnlockModal show={unlockModalOpen} onBack={() => setUnlockModalOpen(false)} error={''} user={user} />
    </div>
  );
};

export default SettingsPage;
